import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const LANGS = ["en", "es", "ru"];
const PAGES = [
  { key: "rental-conditions", label: "Rental Conditions" },
  { key: "terms-and-conditions", label: "Terms & Conditions" },
  { key: "privacy-policy", label: "Privacy Policy" },
  { key: "cookies-policy", label: "Cookies Policy" },
];

export default function LegalPagesAdmin() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeLang, setActiveLang] = useState("en");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    const { data } = await api.get("/legal-pages");
    setPages(data || []);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (key) => {
    const found = pages.find((p) => p.key === key);
    setEditing(found ? JSON.parse(JSON.stringify(found)) : {
      key, title: { en: "", es: "", ru: "" }, seoTitle: { en: "", es: "", ru: "" },
      metaDescription: { en: "", es: "", ru: "" }, content: { en: "", es: "", ru: "" },
      sourceUrl: "", active: true, pendingReview: true,
    });
    setActiveLang("en");
  };

  const save = async () => {
    await api.put(`/legal-pages/${editing.key}`, editing);
    setSaved(true); setTimeout(() => setSaved(false), 1500);
    setEditing(null); load();
  };

  return (
    <div data-testid="admin-legal-page">
      <h1 className="font-display text-[#F5F1EB] text-3xl mb-8">Legal Pages</h1>

      <div className="grid gap-3">
        {PAGES.map(({ key, label }) => {
          const p = pages.find((x) => x.key === key);
          return (
            <div key={key} className="border border-[#2A2A2A] p-4 flex items-center gap-4" data-testid={`legal-row-${key}`}>
              <div className="flex-1">
                <div className="font-display text-[#F5F1EB]">{label}</div>
                <div className="text-xs text-[#8A8A8A] mt-1">/{key}{p?.pendingReview ? " · " : ""}{p?.pendingReview && <span className="text-[#C9A55A]">Pending review</span>}</div>
              </div>
              <button onClick={() => startEdit(key)} className="btn-gold" data-testid={`legal-edit-${key}`}>Edit</button>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[#0C0C0C]/95 z-50 overflow-y-auto" data-testid="legal-editor">
          <div className="max-w-3xl mx-auto p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-[#F5F1EB] text-2xl">Edit · {editing.key}</h2>
              <button onClick={() => setEditing(null)} className="text-[#F5F1EB]">Close</button>
            </div>

            <div className="mb-4 flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 text-[#F5F1EB]">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({...editing, active: e.target.checked})} /> Active
              </label>
              <label className="flex items-center gap-2 text-[#F5F1EB]">
                <input type="checkbox" checked={!!editing.pendingReview} onChange={(e) => setEditing({...editing, pendingReview: e.target.checked})} /> Pending legal review
              </label>
            </div>

            <div className="border border-[#2A2A2A]">
              <div className="flex border-b border-[#2A2A2A]">
                {LANGS.map((l) => (
                  <button key={l} onClick={() => setActiveLang(l)}
                    data-testid={`legal-tab-${l}`}
                    className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-semibold ${activeLang === l ? "bg-[#C9A55A] text-[#0C0C0C]" : "text-[#F5F1EB]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="p-4 space-y-3">
                <Field label="Title" value={editing.title?.[activeLang] || ""} onChange={(v) => setEditing({...editing, title: {...editing.title, [activeLang]: v}})} testid={`legal-title-${activeLang}`} />
                <Field label="SEO title" value={editing.seoTitle?.[activeLang] || ""} onChange={(v) => setEditing({...editing, seoTitle: {...editing.seoTitle, [activeLang]: v}})} testid={`legal-seo-${activeLang}`} />
                <Field label="Meta description" value={editing.metaDescription?.[activeLang] || ""} onChange={(v) => setEditing({...editing, metaDescription: {...editing.metaDescription, [activeLang]: v}})} textarea testid={`legal-meta-${activeLang}`} />
                <div>
                  <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">Content ({activeLang})</label>
                  <textarea rows={18} value={editing.content?.[activeLang] || ""}
                    data-testid={`legal-content-${activeLang}`}
                    onChange={(e) => setEditing({...editing, content: {...editing.content, [activeLang]: e.target.value}})}
                    className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm font-light leading-relaxed" />
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end items-center">
              {saved && <span className="text-[#C9A55A] text-sm">Saved</span>}
              <button onClick={() => setEditing(null)} className="px-5 py-3 border border-[#2A2A2A] text-[#F5F1EB] uppercase text-xs tracking-[0.18em]">Cancel</button>
              <button onClick={save} className="btn-gold" data-testid="legal-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{label}</label>
      {textarea
        ? <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm" />
        : <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm" />}
    </div>
  );
}
